// # Filename: src/components/poker/DesktopPokerTable.jsx
// Desktop (>=1024px) table layout: stadium-ring seat pods around an oval felt, heading
// row with table-link/WS-status, side-by-side action panel + table chat below.

import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import { LuCopy, LuCircleDollarSign } from "react-icons/lu";
import { useNavigate } from "react-router-dom";

import Button from "../ui/Button";
import Tooltip from "../ui/Tooltip";
import { showToast } from "../../utils/toast/Toast";
import PokerCard from "./PokerCard";
import PlayerAvatarFace from "./shared/PlayerAvatarFace";
import SeatRoleChip from "./shared/SeatRoleChip";
import ChipTransferOverlay, { BetMarker } from "./shared/ChipTransferOverlay";
import { FeltInfoPanel, FeltRoundMeta } from "./shared/PokerFeltInfo";
import PokerActionControls from "./shared/PokerActionControls";
import PokerTableChat from "./shared/PokerTableChat";
import { playerMoveLabel, actionLabelClass } from "./shared/pokerGameUtils";
import { stadiumHeroFraction, stadiumPointAtPerpendicularOffset, opponentSeatFraction } from "./shared/stadiumMath";
import { usePokerTableView } from "./hooks/usePokerTableView";

const TABLE_CONTAINER_HALF_WIDTH_PCT = 50;
const TABLE_CONTAINER_HALF_HEIGHT_PCT = 50;
// NOTE: the felt's visual border div (rendered further down) sets its own inset via a
// hardcoded `inset-x-[13%] inset-y-[20%]` Tailwind class rather than reading these
// constants -- keep both in sync manually if either changes, or the rendered rail will
// stop matching where the seat/card/chip math below thinks the felt boundary is.
// (13, not the old 12, because DESKTOP_TABLE_ASPECT_RATIO changed too -- this keeps the
// same left/right-vs-top/bottom rail thickness ratio we already tuned, scaled for the
// new aspect ratio: X_PCT * ratio should stay proportional to Y_PCT.)
const TABLE_FELT_INSET_X_PCT = 13;
const TABLE_FELT_INSET_Y_PCT = 20;
const TABLE_FELT_HALF_WIDTH_PCT = TABLE_CONTAINER_HALF_WIDTH_PCT - TABLE_FELT_INSET_X_PCT;
const TABLE_FELT_HALF_HEIGHT_PCT = TABLE_CONTAINER_HALF_HEIGHT_PCT - TABLE_FELT_INSET_Y_PCT;

const STADIUM_FELT_STRAIGHT_HALF_PCT = TABLE_FELT_HALF_WIDTH_PCT - TABLE_FELT_HALF_HEIGHT_PCT;
const CARD_RING_RADIUS_PCT = TABLE_FELT_HALF_HEIGHT_PCT - 4;
const CHIP_RING_RADIUS_PCT = TABLE_FELT_HALF_HEIGHT_PCT + 10;
const CARD_RING_INSET_FROM_FELT_PCT = TABLE_FELT_HALF_HEIGHT_PCT - CARD_RING_RADIUS_PCT;
const CHIP_RING_STRAIGHT_HALF_PCT = STADIUM_FELT_STRAIGHT_HALF_PCT;
const CHIP_RING_OFFSET_FROM_FELT_PCT = CHIP_RING_RADIUS_PCT - TABLE_FELT_HALF_HEIGHT_PCT;
const CHIP_RING_CAP_MAX_HORIZONTAL_OFFSET_PCT = 5.8;
const STADIUM_HERO_FRACTION = stadiumHeroFraction(STADIUM_FELT_STRAIGHT_HALF_PCT, TABLE_FELT_HALF_HEIGHT_PCT);

const DESKTOP_TABLE_ASPECT_RATIO = 2.2;
// Fallback used only until the page root + heading/controls rows report their real
// measured heights (below) -- avoids a layout flash on first paint. Everything after
// mount uses live measurements instead, so this number never needs to be hand-tuned.
const DESKTOP_TABLE_VERTICAL_RESERVE_FALLBACK_PX = 364;
// The two inter-row gaps (heading->table, table->controls) come from the container's
// `gap-3` utility (12px) at the md+ breakpoint -- a real fixed value, not a guess.
const DESKTOP_TABLE_ROW_GAP_RESERVE_PX = 24;
// IMPORTANT: this is deliberately NOT `100dvh`. This page's own root is `h-full
// min-h-0`, so the browser already hands it the correct real available height through
// the normal layout cascade (navbar + LayoutFrame's own padding/panel chrome + this
// page's own heading/controls rows all already subtracted) -- `100dvh` ignores every
// bit of that and overestimates space on short viewports (the "small laptop" bug:
// table not scaled down enough, rendered off-center because it overflowed and got
// symmetrically clipped by the scroll container's flex centering). Reading the root's
// own measured height instead means this never drifts out of sync with LayoutFrame
// again, no matter how its chrome changes later.
function desktopTableWidthForReserve(availableHeightPx, localReservePx) {
  return `min(100%, clamp(760px, 92vw, 1280px), calc((${availableHeightPx}px - ${localReservePx}px) * ${DESKTOP_TABLE_ASPECT_RATIO}))`;
}

// Hole cards snap between PokerCard's discrete size variants based on the table's
// actual measured width (same container-type:inline-size element the cqw units
// elsewhere are based on) instead of scaling continuously. Continuous scaling let a
// hole card's outer box shrink below whichever variant's tested proportions it was
// using, clipping the bottom rank/suit corner once the fixed internal padding/font
// size no longer fit the shrunk box.
const HOLE_CARD_SIZE_BREAKPOINTS_PX = { default: 1600, small: 1400, compact: 1281 };
function holeCardVariantForTableWidth(widthPx) {
  if (widthPx >= HOLE_CARD_SIZE_BREAKPOINTS_PX.default) return "default";
  if (widthPx >= HOLE_CARD_SIZE_BREAKPOINTS_PX.small) return "small";
  if (widthPx >= HOLE_CARD_SIZE_BREAKPOINTS_PX.compact) return "compact";
  return "mini";
}
const HOLE_CARD_SIZE_PROPS = {
  default: {},
  small: { small: true },
  compact: { compact: true },
  mini: { mini: true },
};

const BET_MARKER_OFFSET_X_CLAMP = { minPx: 36, preferredCqw: 4.4, maxPx: 52 };
const BET_MARKER_OFFSET_Y_CLAMP = { minPx: 30, preferredCqw: 3.7, maxPx: 44 };

function signedClampLength(value, { minPx, preferredCqw, maxPx }) {
  const magnitude = Math.abs(Number(value) || 0);
  if (magnitude < 0.001) return "0px";
  const clampValue = `clamp(${(minPx * magnitude).toFixed(2)}px, ${(preferredCqw * magnitude).toFixed(3)}cqw, ${(maxPx * magnitude).toFixed(2)}px)`;
  return value < 0 ? `calc(0px - ${clampValue})` : clampValue;
}

function stadiumPositionStyle(point) {
  return {
    left: `${50 + point.xPct}%`,
    top: `${50 + point.yPct}%`,
  };
}

function pointToPercent(point) {
  return {
    xPct: 50 + point.xPct,
    yPct: 50 + point.yPct,
  };
}

// Manual per-seat corrections, layered on top of the formula-computed position.
// Necessary because the table is a wide 2.37:1 oval: a percentage-point offset moves a
// LEFT/RIGHT (cap) seat mostly horizontally (against the wide dimension -> big pixel
// move) but a NORTH/SOUTH (straight-edge) seat purely vertically (against the short
// dimension -> tiny pixel move). The same numeric radius can't look right everywhere,
// so tune the specific seat that looks off here instead of the shared constants above.
//
// Keyed by opponent index (0 = first opponent seat clockwise from the hero). NOTE:
// which physical position a given index lands on shifts with the opponent count (index
// 1 is "north" at a 3-handed table but not necessarily at a 6-handed one) -- these are
// tuned for the count you're actively testing with; revisit if you change player counts.
// { card: {x, y} } in percentage points, added directly to the computed card point.
const SEAT_CARD_NUDGE_PCT = {
  // 0: { card: { x: 0, y: 0 } },
  // 1: { card: { x: 0, y: -3 } }, // e.g. pull a north seat's cards back off the border
  // 2: { card: { x: 0, y: 0 } },
};

function nudgeSeatPoint(point, index, key) {
  const nudge = SEAT_CARD_NUDGE_PCT[index]?.[key];
  if (!nudge) return point;
  return { ...point, xPct: point.xPct + (nudge.x || 0), yPct: point.yPct + (nudge.y || 0) };
}

function desktopSeatPoint(index, count) {
  const point = stadiumPointAtPerpendicularOffset(
    opponentSeatFraction(index, count, STADIUM_HERO_FRACTION),
    CHIP_RING_STRAIGHT_HALF_PCT,
    TABLE_FELT_HALF_HEIGHT_PCT,
    CHIP_RING_OFFSET_FROM_FELT_PCT,
    CHIP_RING_CAP_MAX_HORIZONTAL_OFFSET_PCT
  );
  return nudgeSeatPoint(point, index, "chip");
}

function desktopSeatPosition(index, count) {
  return stadiumPositionStyle(desktopSeatPoint(index, count));
}

function desktopFeltCardPoint(index, count) {
  const point = stadiumPointAtPerpendicularOffset(
    opponentSeatFraction(index, count, STADIUM_HERO_FRACTION),
    STADIUM_FELT_STRAIGHT_HALF_PCT,
    TABLE_FELT_HALF_HEIGHT_PCT,
    -CARD_RING_INSET_FROM_FELT_PCT
  );
  return nudgeSeatPoint(point, index, "card");
}

function desktopHeroFeltCardPoint() {
  return stadiumPointAtPerpendicularOffset(
    STADIUM_HERO_FRACTION,
    STADIUM_FELT_STRAIGHT_HALF_PCT,
    TABLE_FELT_HALF_HEIGHT_PCT,
    -CARD_RING_INSET_FROM_FELT_PCT
  );
}

function betMarkerStyleForStadiumPoint(point) {
  const offsetX = signedClampLength(point.inwardX, BET_MARKER_OFFSET_X_CLAMP);
  const offsetY = signedClampLength(point.inwardY, BET_MARKER_OFFSET_Y_CLAMP);
  return {
    transform: `translate(calc(-50% + ${offsetX}), calc(-50% + ${offsetY}))`,
  };
}

// The card cluster (2 cards side by side) is wider than it is tall, so a flat pixel
// offset can never give equal clearance in every direction -- it clears easily along
// the wide axis and grazes the cards along the short one, which is exactly the
// "touching for some seats, not others" inconsistency. Model the cluster as an ellipse
// (half-width/half-height in real px) instead, and always place the badge GAP_PX
// outside *that* boundary, whichever direction it's travelling -- same visual
// clearance from the cards at every seat, by construction, not by tuning a constant.
const ROLE_CHIP_CARD_HALF_WIDTH_PX = 50;
const ROLE_CHIP_CARD_HALF_HEIGHT_PX = 24;
const ROLE_CHIP_GAP_PX = 12;

// betMarkerStyleForStadiumPoint pushes straight toward the felt center (point.inwardX/
// inwardY) -- correct for a bet marker traveling toward the pot. The role chip badge
// blends that SAME inward direction (so it moves toward center and away from the
// CasinoSeatPod nameplate, which sits purely outward, past the felt edge) with a
// tangential (perpendicular) component, so it lands in a corner near the cards --
// biased toward center, offset to the side -- instead of directly on the bet marker's
// own inward line. extraPx is a last-resort per-seat nudge (see SEAT_CARD_NUDGE_PCT)
// if a specific seat still needs more separation.
function roleChipStyleForStadiumPoint(point, extraPx = { x: 0, y: 0 }) {
  const inwardX = point.inwardX || 0;
  const inwardY = point.inwardY || 0;
  const tangentX = -inwardY;
  const tangentY = inwardX;
  const dirX = inwardX * 0.6 + tangentX * 0.7;
  const dirY = inwardY * 0.6 + tangentY * 0.7;
  const magnitude = Math.hypot(dirX, dirY) || 1;
  const normX = dirX / magnitude;
  const normY = dirY / magnitude;
  const ellipseDistance = 1 / Math.sqrt(
    (normX / ROLE_CHIP_CARD_HALF_WIDTH_PX) ** 2 + (normY / ROLE_CHIP_CARD_HALF_HEIGHT_PX) ** 2
  );
  const distance = ellipseDistance + ROLE_CHIP_GAP_PX;
  const offsetX = `${(normX * distance + (extraPx.x || 0)).toFixed(2)}px`;
  const offsetY = `${(normY * distance + (extraPx.y || 0)).toFixed(2)}px`;
  return {
    transform: `translate(calc(-50% + ${offsetX}), calc(-50% + ${offsetY}))`,
  };
}

function CasinoSeatPod({
  player,
  active,
  dealer,
  isMe = false,
  className = "",
  handNumber = 1,
  showCards = true,
  moveLabel = "",
  timerRemaining = null,
  timerPercent = 0,
  timerUrgent = false,
  style = undefined,
  displayChips = null,
  payoutPulse = false,
  stackAmountRef = null,
}) {
  const showTimer = active && timerRemaining != null;
  const statusText = moveLabel || (active ? "To act" : player.folded ? "Fold" : player.allIn ? "All in" : player.best || "Waiting");

  return (
    <div
      className={[
        "pointer-events-auto absolute flex w-[clamp(96px,11.1cqw,142px)] -translate-x-1/2 flex-col items-center",
        className,
      ].join(" ")}
      style={style}
    >
      <div
        className={[
          "relative flex h-[clamp(40px,4.55cqw,58px)] w-full items-center gap-[clamp(4px,0.47cqw,6px)] rounded-sm border px-[clamp(4px,0.47cqw,6px)] py-[clamp(3px,0.31cqw,4px)] text-left shadow-[0_10px_22px_rgba(0,0,0,0.32)]",
          active
            ? "border-emerald-200/45 bg-[linear-gradient(180deg,rgba(16,185,129,0.26),rgba(6,78,59,0.96))] shadow-[0_0_24px_rgba(16,185,129,0.22),0_10px_22px_rgba(0,0,0,0.32)]"
            : payoutPulse
              ? "border-amber-200/60 bg-[linear-gradient(180deg,rgba(251,191,36,0.22),rgba(69,26,3,0.96))] shadow-[0_0_30px_rgba(251,191,36,0.38),0_10px_22px_rgba(0,0,0,0.32)]"
            : "border-emerald-200/[0.12] bg-[linear-gradient(180deg,rgba(8,47,37,0.94),rgba(12,28,24,0.98))]",
          player.folded ? "opacity-55" : "",
        ].join(" ")}
      >
        <PlayerAvatarFace seat={player.seat} isMe={isMe} />
        <div className="flex min-w-0 flex-1 flex-col justify-center">
          <div className="truncate text-[clamp(8px,0.9cqw,12px)] font-black uppercase tracking-[0.04em] text-emerald-50/95">
            {isMe ? "You" : player.name || "Player"}
          </div>
          <div className="mt-0.5 flex items-center gap-1 truncate text-[clamp(8px,0.9cqw,12px)] font-black text-amber-50">
            <LuCircleDollarSign size="clamp(12px,1.25cqw,16px)" className="shrink-0 text-amber-100" />
            <span ref={stackAmountRef} className="truncate">{displayChips ?? player.chips}</span>
          </div>
          {showCards ? (
            <div className="mt-0.5 flex justify-start gap-0.5">
              {(player.cards || []).map((card, idx) => (
                <PokerCard
                  key={`${handNumber}-${player.seat || player.name || "casino"}-${idx}-${card}`}
                  card={card}
                  mini
                  animate={Boolean(card)}
                  dealVariant="hole"
                  delay={idx * 90 + 80}
                />
              ))}
            </div>
          ) : null}
          <div className={["mt-0.5 h-[clamp(10px,1.1cqw,14px)] truncate rounded-sm px-[clamp(3px,0.31cqw,4px)] py-0.5 text-[clamp(7px,0.78cqw,10px)] font-black uppercase leading-none tracking-[0.04em]", actionLabelClass(statusText, active)].join(" ")}>
            {statusText}
          </div>
          {showTimer ? (
            <div className="mt-0.5 overflow-hidden rounded-full bg-black/35">
              <div
                className={[timerUrgent ? "bg-rose-300" : "bg-emerald-200", "h-1 rounded-full transition-[width] duration-200"].join(" ")}
                style={{ width: `${timerPercent}%` }}
              />
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function FeltHoleCards({
  player,
  className = "",
  handNumber = 1,
  roleLabel = "",
  style = undefined,
  faceDown = false,
  betMarkerStyle = undefined,
  roleChipClassName = "left-1/2 top-1/2",
  roleChipStyle = undefined,
  elasticRoleChip = false,
  sizeVariant = "mini",
}) {
  if (!player) return null;
  const cards = player.cards?.length ? player.cards : ["??", "??"];
  const hasRealHiddenCards = Boolean(player.cards?.length);
  const folded = Boolean(player.folded);
  const sizeProps = HOLE_CARD_SIZE_PROPS[sizeVariant] || HOLE_CARD_SIZE_PROPS.mini;

  return (
    <div
      className={["pointer-events-none absolute z-20 flex -translate-x-1/2 items-center gap-0.5", className].join(" ")}
      style={style}
    >
      <SeatRoleChip label={roleLabel} className={roleChipClassName} style={roleChipStyle} elastic={elasticRoleChip} />
      <div className="relative flex items-center gap-[clamp(1px,0.16cqw,2px)]">
        <div className={["flex items-center gap-[clamp(1px,0.16cqw,2px)]", folded ? "opacity-55 saturate-50" : ""].join(" ")}>
          {cards.map((card, idx) => (
            <PokerCard
              key={`${handNumber}-${player.seat || player.name || "felt"}-${idx}-${card}`}
              card={card}
              {...sizeProps}
              faceDown={faceDown && hasRealHiddenCards && card === "??" && !folded}
              animate={Boolean(card)}
              dealVariant="hole"
              delay={idx * 90 + 80}
            />
          ))}
        </div>
        {folded ? (
          <span className="absolute left-1/2 top-1/2 z-40 -translate-x-1/2 -translate-y-1/2 -rotate-12 rounded-sm border border-rose-200/35 bg-rose-950/82 px-[clamp(6px,0.63cqw,8px)] py-0.5 text-[clamp(6px,0.63cqw,8px)] font-black uppercase tracking-[0.16em] text-rose-100 shadow-[0_6px_18px_rgba(0,0,0,0.42)]">
            Folded
          </span>
        ) : null}
      </div>
      <BetMarker
        amount={player.bet}
        name={player.name}
        className="absolute left-1/2 top-1/2 z-30"
        style={betMarkerStyle}
      />
    </div>
  );
}

function useDesktopTableSizing() {
  const [tableWidthPx, setTableWidthPx] = useState(0);
  const [chromeReservePx, setChromeReservePx] = useState(DESKTOP_TABLE_VERTICAL_RESERVE_FALLBACK_PX);
  const [availableHeightPx, setAvailableHeightPx] = useState(() => (
    typeof window !== "undefined" ? window.innerHeight : 900
  ));
  const pageRootRef = useRef(null);
  const headingRowRef = useRef(null);
  const controlsRowRef = useRef(null);

  const attachTableRef = useCallback((tableRef) => {
    const node = tableRef.current;
    if (!node || typeof ResizeObserver === "undefined") return undefined;
    const measure = () => setTableWidthPx(node.getBoundingClientRect().width);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  // Keeps the table's size in sync with how much vertical space the heading row +
  // controls/chat row actually take -- instead of guessing at a fixed reserve, so the
  // two can never drift out of sync as either row's content changes.
  useLayoutEffect(() => {
    if (typeof ResizeObserver === "undefined") return undefined;
    const measure = () => {
      const headingHeight = headingRowRef.current?.getBoundingClientRect().height || 0;
      const controlsHeight = controlsRowRef.current?.getBoundingClientRect().height || 0;
      if (!headingHeight && !controlsHeight) return;
      setChromeReservePx(headingHeight + controlsHeight + DESKTOP_TABLE_ROW_GAP_RESERVE_PX);
    };
    measure();
    const observer = new ResizeObserver(measure);
    if (headingRowRef.current) observer.observe(headingRowRef.current);
    if (controlsRowRef.current) observer.observe(controlsRowRef.current);
    return () => observer.disconnect();
  }, []);

  // Measures this page's own real available height (already net of the navbar,
  // LayoutFrame's padding/panel chrome, everything) instead of trusting `100dvh`,
  // which ignores all of that and overestimates space on short viewports.
  useLayoutEffect(() => {
    const node = pageRootRef.current;
    if (!node || typeof ResizeObserver === "undefined") return undefined;
    const measure = () => {
      const height = node.getBoundingClientRect().height;
      if (height) setAvailableHeightPx(height);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const desktopTableWidth = useMemo(
    () => desktopTableWidthForReserve(availableHeightPx, chromeReservePx),
    [availableHeightPx, chromeReservePx]
  );
  const holeCardVariant = useMemo(
    () => holeCardVariantForTableWidth(tableWidthPx),
    [tableWidthPx]
  );

  return { pageRootRef, headingRowRef, controlsRowRef, attachTableRef, desktopTableWidth, holeCardVariant };
}

export default function DesktopPokerTable({ game, handResultAnimation = null, wsStatus, onAction, onNextHand, onPlayAgain }) {
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const { pageRootRef, headingRowRef, controlsRowRef, attachTableRef, desktopTableWidth, holeCardVariant } = useDesktopTableSizing();

  const getChipFallbackPoint = useCallback(({ seat, isHero, opponentIndex, opponentCount }) => {
    if (isHero) return { xPct: 50, yPct: 92 };
    return pointToPercent(desktopSeatPoint(opponentIndex, opponentCount));
  }, []);

  const view = usePokerTableView({ game, handResultAnimation, onAction, getChipFallbackPoint });
  const {
    tableRef,
    registerStackAmountRef,
    mySeat,
    tablePlayers,
    displayedCommunityCards,
    me,
    heroEliminated,
    heroFeltPlayer,
    opponents,
    chipCountOverrides,
    potCountOverride,
    chipTransferStreams,
    payoutPulseSeats,
    isMyTurn,
    can,
    minRaiseTo,
    maxRaiseTo,
    timerRemaining,
    timerPercent,
    timerUrgent,
    roleForSeat,
    pendingLabelForSeat,
    raiseTo,
    setRaiseTo,
    raisePanelOpen,
    setRaisePanelOpen,
    canRaiseOrAllIn,
    raisePresets,
    handleAction,
  } = view;

  useLayoutEffect(() => attachTableRef(tableRef), [attachTableRef, tableRef]);

  const heroCardPoint = useMemo(() => desktopHeroFeltCardPoint(), []);

  const handleCopyTableLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      showToast("success", "Table link copied.");
      window.setTimeout(() => setCopied(false), 1400);
    } catch {
      showToast("error", "Could not copy table link.");
    }
  };

  return (
    <div ref={pageRootRef} className="mx-auto flex h-full min-h-0 w-full max-w-[1280px] flex-col gap-2 pb-[calc(env(safe-area-inset-bottom)+150px)] sm:gap-3 sm:pb-0">
      <div ref={headingRowRef} className="flex shrink-0 flex-col gap-3 px-1 md:flex-row md:items-center md:justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-[0.28em] text-amber-200/75">No-limit Texas Hold'em</div>
          <h1 className="text-2xl font-semibold text-text-primary">Poker Table</h1>
          <div className="mt-1 text-[11px] text-text-secondary">
            Hand {game?.hand_number || 1} • Dealer: {tablePlayers.find((p) => Number(p.seat) === Number(game?.dealer))?.name || "Seat"}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Tooltip content="Copy table link">
            <Button type="button" variant="outline" size="sm" onClick={handleCopyTableLink}>
              <LuCopy size={15} />
              {copied ? "Copied" : "Table Link"}
            </Button>
          </Tooltip>
          {wsStatus ? (
            <span className="rounded-full border border-emerald-300/20 bg-emerald-300/10 px-2.5 py-1 text-[11px] font-semibold text-emerald-200">
              WS: {wsStatus === "connected" ? "LIVE" : wsStatus}
            </span>
          ) : null}
        </div>
      </div>

      <div className="flex flex-1 min-h-0 flex-col items-center justify-center gap-2 sm:gap-3">
      <div
        ref={tableRef}
        className="relative mx-auto aspect-[var(--desktop-poker-table-ratio)] h-auto w-[var(--desktop-poker-table-w)] min-h-0 flex-none overflow-hidden rounded-full border border-emerald-200/15 bg-[#1b1009] shadow-[0_28px_90px_rgba(0,0,0,0.5)] [container-type:inline-size]"
        style={{
          "--desktop-poker-table-ratio": DESKTOP_TABLE_ASPECT_RATIO,
          "--desktop-poker-table-w": desktopTableWidth,
        }}
      >
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_24%,rgba(120,64,24,0.5),transparent_34%),linear-gradient(90deg,rgba(40,20,10,0.96),rgba(88,45,19,0.88),rgba(28,14,8,0.96))]" />
        <div className="absolute inset-x-[13%] inset-y-[20%] rounded-full border-[10px] border-[#8a4b18] bg-[radial-gradient(ellipse_at_50%_48%,rgba(22,101,52,0.98),rgba(5,76,44,0.94)_52%,rgba(3,49,32,0.98)_100%)] shadow-[inset_0_0_0_2px_rgba(250,204,21,0.25),inset_0_0_0_8px_rgba(15,23,42,0.28),inset_0_24px_58px_rgba(0,0,0,0.4),0_18px_38px_rgba(0,0,0,0.5)]" />

        <div className="pointer-events-none absolute inset-0 z-20">
          {opponents.slice(0, 8).map((opponent, idx) => {
            const seatPosition = desktopSeatPosition(idx, opponents.length);
            const pendingLabel = pendingLabelForSeat(opponent.seat);
            const active = Number(game?.current_turn) === Number(opponent.seat) && !game?.is_completed;
            return (
              <CasinoSeatPod
                key={opponent.seat}
                player={opponent}
                active={active}
                dealer={Number(game?.dealer) === Number(opponent.seat)}
                className="-translate-y-1/2"
                style={seatPosition}
                handNumber={game?.hand_number || 1}
                showCards={false}
                moveLabel={pendingLabel || playerMoveLabel(opponent, active, game)}
                timerRemaining={timerRemaining}
                timerPercent={timerPercent}
                timerUrgent={timerUrgent}
                displayChips={chipCountOverrides[opponent.seat]}
                payoutPulse={payoutPulseSeats.has(Number(opponent.seat))}
                stackAmountRef={registerStackAmountRef(opponent.seat)}
              />
            );
          })}
          {opponents.slice(0, 8).map((opponent, idx) => {
            const cardPoint = desktopFeltCardPoint(idx, opponents.length);
            return (
              <FeltHoleCards
                key={`felt-${opponent.seat}`}
                player={opponent}
                className="-translate-y-1/2"
                style={stadiumPositionStyle(cardPoint)}
                handNumber={game?.hand_number || 1}
                roleLabel={roleForSeat(opponent.seat)}
                faceDown
                betMarkerStyle={betMarkerStyleForStadiumPoint(cardPoint)}
                roleChipStyle={roleChipStyleForStadiumPoint(cardPoint, SEAT_CARD_NUDGE_PCT[idx]?.roleChip)}
                elasticRoleChip
                sizeVariant={holeCardVariant}
              />
            );
          })}
          <CasinoSeatPod
            player={heroFeltPlayer}
            active={!heroEliminated && isMyTurn}
            dealer={!heroEliminated && Number(game?.dealer) === Number(mySeat)}
            isMe
            className="bottom-5 left-1/2"
            handNumber={game?.hand_number || 1}
            showCards={false}
            moveLabel={heroEliminated ? "Out" : pendingLabelForSeat(me?.seat) || playerMoveLabel(me, isMyTurn, game)}
            timerRemaining={timerRemaining}
            timerPercent={timerPercent}
            timerUrgent={timerUrgent}
            displayChips={chipCountOverrides[me?.seat]}
            payoutPulse={payoutPulseSeats.has(Number(me?.seat))}
            stackAmountRef={registerStackAmountRef(me?.seat)}
          />
          {!heroEliminated ? (
            <FeltHoleCards
              player={heroFeltPlayer}
              className="-translate-y-1/2"
              style={stadiumPositionStyle(heroCardPoint)}
              handNumber={game?.hand_number || 1}
              roleLabel={roleForSeat(me?.seat)}
              betMarkerStyle={betMarkerStyleForStadiumPoint(heroCardPoint)}
              roleChipStyle={roleChipStyleForStadiumPoint(heroCardPoint)}
              elasticRoleChip
              sizeVariant={holeCardVariant}
            />
          ) : null}
        </div>

        <div className="pointer-events-none absolute inset-0 z-10">
          <div className="absolute left-1/2 top-[calc(50%-clamp(38px,4.2cqw,54px))] -translate-x-1/2">
            <FeltRoundMeta game={game} />
          </div>
          <div className="absolute left-1/2 top-1/2 flex min-h-[clamp(54px,5.8cqw,74px)] -translate-x-1/2 -translate-y-1/2 items-center justify-center gap-1.5 px-1 py-2 [&>div]:h-[clamp(43px,5cqw,64px)] [&>div]:w-[clamp(30px,3.45cqw,44px)]">
            {Array.from({ length: 5 }).map((_, idx) => (
              <PokerCard
                key={`${game?.hand_number || 1}-${idx}-${displayedCommunityCards?.[idx] || "empty"}`}
                card={displayedCommunityCards?.[idx]}
                compact
                animate={Boolean(displayedCommunityCards?.[idx])}
                dealVariant="community"
                delay={idx * 90}
              />
            ))}
          </div>
          <div className="absolute left-1/2 top-[calc(50%+clamp(32px,3.45cqw,44px))] -translate-x-1/2">
            <FeltInfoPanel game={game} displayPot={potCountOverride} />
          </div>
        </div>
        <ChipTransferOverlay streams={chipTransferStreams} />
      </div>

      <div ref={controlsRowRef} className="mx-auto flex w-fit flex-row items-start gap-3">
        <PokerActionControls
          game={game}
          raiseTo={raiseTo}
          setRaiseTo={setRaiseTo}
          raisePanelOpen={raisePanelOpen}
          setRaisePanelOpen={setRaisePanelOpen}
          raisePresets={raisePresets}
          minRaiseTo={minRaiseTo}
          maxRaiseTo={maxRaiseTo}
          can={can}
          canRaiseOrAllIn={canRaiseOrAllIn}
          handleAction={handleAction}
          onNextHand={onNextHand}
          onPlayAgain={onPlayAgain}
          navigate={navigate}
        />
        <PokerTableChat gameId={game?.id} />
      </div>
      </div>
    </div>
  );
}
